import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { studentApi } from '@/api';
import { Button, Icon, useToast } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { cn } from '@/utils/cn';

/** Shared with PortfolioShareCard, so both always show the same state. */
const QUERY_KEY = ['portfolio-sharing'];

function absoluteUrl(path) {
  if (!path || typeof window === 'undefined') return path ?? '';
  return new URL(path, window.location.origin).toString();
}

/**
 * The student's portfolio link in one line, for the top of the dashboard and
 * the profile: the link with Copy and Open while sharing is on, a button to
 * turn it on while it is off. Turning it off, or getting a new link, stays on
 * the full "Shareable portfolio" card on the profile.
 */
export function PortfolioLinkBar({ className }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data, isPending, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: studentApi.getPortfolioSharing,
  });

  const enable = useMutation({
    mutationFn: () => studentApi.setPortfolioSharing(true),
    onSuccess: (sharing) => {
      queryClient.setQueryData(QUERY_KEY, sharing);
      toast.success('Your portfolio link is live');
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not turn on sharing')),
  });

  // Quietly absent while loading or on error: it is a shortcut, not the page.
  if (isPending || isError || !data) return null;

  const url = absoluteUrl(data.path);
  const live = data.enabled && data.path;

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Portfolio link copied');
    } catch {
      toast.error('Could not copy. Select the link and copy it yourself.');
    }
  }

  return (
    <div
      className={cn(
        'border-brand-200 bg-brand-50/60 rounded-card flex flex-wrap items-center gap-x-4 gap-y-2 border px-4 py-3',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className="bg-surface text-brand-600 ring-brand-100 grid size-9 shrink-0 place-items-center rounded-lg ring-1"
      >
        <Icon name="send" size={17} />
      </span>
      <div className="min-w-0 flex-[1_1_14rem]">
        <p className="text-ink text-sm font-semibold">Your portfolio link</p>
        {live ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer noopener"
            className="text-brand-700 block truncate text-sm hover:underline"
            title={url}
          >
            {url}
          </a>
        ) : (
          <p className="text-ink-muted text-sm">
            Share a public page of your profile and CV with companies.
          </p>
        )}
      </div>
      <div className="flex shrink-0 flex-wrap gap-2">
        {live ? (
          <>
            <Button size="sm" onClick={() => void copy()}>
              <Icon name="clipboard" size={15} />
              Copy link
            </Button>
            <a
              href={url}
              target="_blank"
              rel="noreferrer noopener"
              className="border-line bg-surface text-ink hover:border-brand-300 hover:text-brand-700 rounded-input inline-flex min-h-8 items-center gap-1.5 border px-3 py-1 text-sm font-medium shadow-xs transition"
            >
              <Icon name="external" size={15} />
              Open
            </a>
          </>
        ) : (
          <Button size="sm" isLoading={enable.isPending} onClick={() => enable.mutate()}>
            <Icon name="send" size={15} />
            Create my link
          </Button>
        )}
      </div>
    </div>
  );
}
